import { Link } from "react-router-dom";
import { getBookCovers, getBookIdentity } from "./bookContent";
import ProtectedPreview from "./ProtectedPreview";
import "./BookPresentation.css";
import { useLanguage } from "../shared/LanguageContext";
import { websiteCopy } from "../shared/websiteCopy";

function BookPresentation() {
  const { language } = useLanguage();
  const copy = websiteCopy[language].book;
  const bookIdentity = getBookIdentity(language);
  const covers = getBookCovers(language);
  const paymentLink = language === "es"
    ? process.env.REACT_APP_STRIPE_PAYMENT_LINK_ES
    : process.env.REACT_APP_STRIPE_PAYMENT_LINK;

  return (
    <main className="book-presentation" data-testid="book-presentation">
      <header className="book-presentation__header">
        <p className="eyebrow">{copy.eyebrow}</p>
        <h1>{bookIdentity.title}</h1>
        <p className="book-presentation__series">
          {bookIdentity.series} · {bookIdentity.volumes}
        </p>
        <p className="book-presentation__framing">
          {bookIdentity.framing[0]} · {bookIdentity.framing[1]}
        </p>
        <p className="book-presentation__author">{language === "es" ? "Por" : "By"} {bookIdentity.author}</p>
      </header>

      {/* FRONT COVER → SPINE (approved locked assets) */}
      <section className="book-section" aria-labelledby="covers-heading" data-testid="section-covers">
        <h2 id="covers-heading" className="book-section__title">{copy.frontCover}</h2>
        <div className="cover-row">
          <figure className="cover-figure" data-testid="cover-front">
            <img className="cover-img cover-img--front" src={covers.front.src} alt={covers.front.alt} />
            <figcaption>{copy.frontCover}</figcaption>
          </figure>
          <figure className="cover-figure cover-figure--spine" data-testid="cover-spine">
            <img className="cover-img cover-img--spine" src={covers.spine.src} alt={covers.spine.alt} />
            <figcaption>{copy.spine}</figcaption>
          </figure>
        </div>
      </section>

      {/* BACK COVER (corrected asset — Packet 0010) */}
      <section className="book-section" aria-labelledby="back-cover-heading" data-testid="section-back-cover">
        <h2 id="back-cover-heading" className="book-section__title">{copy.backCover}</h2>
        <div className="cover-row">
          <figure className="cover-figure" data-testid="cover-back">
            <img className="cover-img cover-img--back" src={covers.back.src} alt={covers.back.alt} />
            <figcaption>{copy.backCover}</figcaption>
          </figure>
        </div>
      </section>

      {/* AUTHORIZED PREVIEW — Packet 0010: rendered directly from the master manuscript
          (PDF pages 1–16), replacing the retyped-text preview. No reconstruction, no OCR,
          no substitution. Navigation is hard-bounded to page 16; Chapter 2 is absent. */}
      <section className="book-section" aria-labelledby="preview-heading" data-testid="section-preview">
        <h2 id="preview-heading" className="book-section__title">{copy.authorizedPreview}</h2>
        <p className="book-presentation__preview-note">
          {copy.previewNote}
        </p>
        <ProtectedPreview />
      </section>

      {/* CONTINUATION / PURCHASE — active purchase-continuation path (Packet 007/008) */}
      <section className="book-section continuation" aria-labelledby="continuation-heading" data-testid="section-continuation">
        <h2 id="continuation-heading" className="book-section__title">{copy.continueJourney}</h2>
        <p className="continuation__note">
          {copy.continuationNote}
        </p>
        <p className="continuation__statement" data-testid="continuation-statement">
          {copy.continuationStatement}
        </p>
        <div className="continuation__actions" data-testid="continuation-actions">
          <a
            href={paymentLink}
            target="_blank"
            rel="noopener noreferrer"
            className="continuation__cta continuation__cta--active"
            data-testid="purchase-continue-reading"
          >
            {copy.purchaseContinue}
          </a>
          <Link
            to="/mirror-of-the-mind/book/product"
            className="continuation__product-link"
            data-testid="view-product-information"
          >
            {copy.productInformation}
          </Link>
        </div>
      </section>

      <nav className="book-presentation__nav" data-testid="book-presentation-nav">
        <Link to="/mirror-of-the-mind" className="nav-link" data-testid="back-to-module">
          {copy.backToAur}
        </Link>
      </nav>
    </main>
  );
}

export default BookPresentation;
